import { useMemo, useState, type FormEvent } from "react";
import { useMe, useSession } from "../../app/session";
import { Banner } from "../../components/ui/Banner";
import { Button } from "../../components/ui/Button";
import { Card, CardHeader } from "../../components/ui/Card";
import { Checkbox, Field, Input, Select } from "../../components/ui/Field";
import { useAction } from "../../hooks/useAction";
import { accountApi } from "../../lib/api/account";

const browserZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export function ProfileCard() {
  const me = useMe();
  const { setMe } = useSession();
  const [name, setName] = useState(me.user.name);
  const [channelName, setChannel] = useState(me.profile?.channelName ?? "");
  const [timezone, setTimezone] = useState(me.profile?.timezone ?? browserZone());
  const [marketingOptIn, setMarketingOptIn] = useState(me.profile?.marketingOptIn ?? false);
  const [saved, setSaved] = useState(false);
  // The list some browsers return leaves out "UTC", the API's default; keep the saved value selectable.
  const zones = useMemo(() => {
    const all = Intl.supportedValuesOf("timeZone");
    return all.includes(timezone) ? all : [timezone, ...all];
  }, [timezone]);

  const save = useAction(async () => {
    setMe(await accountApi.update({ name: name.trim(), channelName: channelName.trim() || null, timezone, marketingOptIn }));
    setSaved(true);
  });
  const submit = (e: FormEvent) => { e.preventDefault(); setSaved(false); void save.run(); };

  return (
    <Card>
      <CardHeader title="Profile" caption={me.user.email} />
      <form onSubmit={submit} className="grid gap-5 p-5 md:grid-cols-2">
        <Field label="name">
          {({ id }) => <Input id={id} required maxLength={100} autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label="channel name (optional)">
          {({ id }) => <Input id={id} maxLength={100} value={channelName} onChange={(e) => setChannel(e.target.value)} />}
        </Field>
        <Field label="time zone" hint="Used for daily usage and schedules.">
          {({ id, describedBy }) => (
            <Select id={id} value={timezone} aria-describedby={describedBy} onChange={(e) => setTimezone(e.target.value)}>
              {zones.map((z) => <option key={z} value={z}>{z}</option>)}
            </Select>
          )}
        </Field>
        <div className="flex flex-col gap-1.5 md:col-span-2">
          <Checkbox checked={marketingOptIn} onChange={(e) => setMarketingOptIn(e.target.checked)} label="Email me product news and offers" />
          <span className="text-xs text-muted">Emails about your account, billing and changes to the service come either way.</span>
        </div>
        <div className="flex flex-col justify-end gap-3 md:col-span-2">
          {save.error && <Banner tone="bad">{save.error}</Banner>}
          {saved && <Banner tone="ok">Saved.</Banner>}
          <div><Button type="submit" variant="primary" pending={save.pending} pendingLabel="saving…">save profile</Button></div>
        </div>
      </form>
    </Card>
  );
}
